import {
    fileBytesToFortuneSheets,
    buildXlsxBuffer as buildXlsxBufferOnMainThread,
    downloadAsXlsx as downloadAsXlsxOnMainThread,
    downloadXlsxBuffer,
    toCelldataSheet,
} from './converters';
import { checkSheetSize, mountedGridCounts, isDenseGridTooLarge, SheetTooLargeError, tooLargeBasis } from './sheetSizeGuard';
import { logSheetLoadFailure, SheetLoadContext, SheetLoadStage } from './sheetLoadTelemetry';
import type { FortuneSheet } from './types';
import type { SheetWorkerRequest, SheetWorkerResponse } from './sheetWorkerProtocol';
import { createParseAssembler } from './sheetWorkerParts';
import { deepFreezePlain } from './deepFreeze';

/** Every response variant EXCEPT the `ready` handshake, which never resolves
 *  a job (it is handled separately, as the boot signal) — narrows `.ok`/
 *  `.error`/etc accesses below, which the id-less `ready` message lacks. */
type SheetWorkerJobResponse = Exclude<SheetWorkerResponse, { kind: 'ready' }>;

/** The worker script itself never evaluated / posted `ready` before erroring
 *  (CSP, a post-deploy chunk 404, a syntax error) — every job it was ever
 *  handed is presumed lost. */
export class SheetWorkerBootError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'SheetWorkerBootError';
    }
}
/** The worker had already posted `ready` (so it booted fine) when it errored
 *  mid-job — its in-flight jobs are presumed lost, but re-running the same
 *  parse on the main thread is NOT attempted. */
export class SheetWorkerCrashError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'SheetWorkerCrashError';
    }
}

// A boot failure only falls back to the main thread for buffers under this
// size — large buffers are exactly the case worker parsing exists to protect
// the main thread from, so re-parsing them there instead just moves the risk.
const WORKER_BOOT_FALLBACK_MAX_BYTES = 2 * 1024 * 1024;

// The build-side twin: a dead or unbootable worker leaves only the main
// thread, which is fine for a small workbook, but re-running a large build
// there (most likely the very job that killed the worker) freezes or crashes the tab.
const BUILD_FALLBACK_MAX_CELLS = 50_000;

/** True when the cells the build would serialize fit under the fallback cap —
 *  counted in converters' getCells order (celldata if non-empty, else the
 *  non-null cells of `data`), decided per sheet because getFullSheets mixes
 *  both shapes. Stops walking as soon as the cap is passed. */
const canBuildOnMainThread = (sheets: FortuneSheet[]): boolean => {
    let count = 0;
    for (const sheet of sheets) {
        if (sheet.celldata?.length) count += sheet.celldata.length;
        else {
            for (const row of sheet.data ?? []) {
                for (const cell of row ?? []) if (cell != null) count += 1;
                if (count > BUILD_FALLBACK_MAX_CELLS) return false;
            }
        }
        if (count > BUILD_FALLBACK_MAX_CELLS) return false;
    }
    return true;
};

/**
 * Worker-backed front door for the heavy spreadsheet conversions. Parsing a
 * large xlsx (unzip + XML DOM + per-cell conversion) blocks the main thread
 * for seconds — long enough for the browser's "Page unresponsive" prompt.
 * These wrappers run the same converters code inside a dedicated worker and
 * fall back to the main-thread implementations when workers are unavailable
 * (jsdom tests) or the worker itself fails to boot — the build wrappers also
 * after a mid-job crash, but only under BUILD_FALLBACK_MAX_CELLS.
 *
 * The worker itself is a lazily-created SINGLETON, reused across every job
 * (parse, build, combined build) instead of a fresh create+terminate per
 * call — re-opening a history version no longer pays worker boot cost on top
 * of the parse. Jobs are queued and posted one at a time (simplest correct:
 * a large parse and a large serialize sharing one worker heap concurrently is
 * a real memory-pressure risk this app hasn't needed to take on); each
 * request/response already carries a monotonic `id` (sheetWorkerProtocol.ts)
 * used to route a response to its own caller regardless of queueing.
 */

let nextJobId = 1;
let sharedWorker: Worker | null = null;
// Set once the CURRENT sharedWorker has posted its top-level `{kind:'ready'}`
// — lets onerror tell a boot failure (never got here) apart from a
// mid-job crash (already ready). Reset whenever a new worker is created,
// so a stale `true` from a since-discarded worker can never leak
// into the classification of the next one.
let workerReady = false;
const pendingJobs = new Map<
    number,
    {
        resolve: (r: SheetWorkerJobResponse) => void;
        reject: (err: unknown) => void;
        onProgress?: (cells: number) => void;
        parts?: ReturnType<typeof createParseAssembler>;
    }
>();
const jobQueue: SheetWorkerRequest[] = [];
let jobInFlight = false;

const postNextJob = () => {
    if (jobInFlight || !sharedWorker) return;
    const request = jobQueue.shift();
    if (!request) return;
    jobInFlight = true;
    sharedWorker.postMessage(request);
};

// The worker died — it failed to load/boot (CSP, chunk 404 after a deploy, …)
// or crashed mid-job — so fail every pending/queued job (each caller decides
// whether to fall back), terminate it, and drop it so the NEXT call creates a
// fresh one (self-healing). A late event from an already-replaced worker is a
// no-op: its jobs were rejected when it was replaced.
const failAllJobs = (worker: Worker, err: Error) => {
    if (worker !== sharedWorker) return;
    pendingJobs.forEach((job) => job.reject(err));
    pendingJobs.clear();
    jobQueue.length = 0;
    jobInFlight = false;
    sharedWorker = null;
    workerReady = false;
    worker.terminate();
};

/**
 * Installs the message/error routing onto an already-created worker and
 * makes it the shared one. Split out from `getSharedWorker` so it can be
 * driven directly against a fake Worker in tests — the real factory needs a
 * genuine dynamic `import()` for `createSheetWorker`'s `import.meta`
 * isolation, which Jest can't execute without `--experimental-vm-modules`
 * (not enabled in this repo), so tests use `__wireSharedWorkerForTests`
 * instead of going through `getSharedWorker`'s import.
 */
const wireSharedWorker = (worker: Worker): Worker => {
    workerReady = false; // A fresh worker always starts unready.
    worker.onmessage = (e: MessageEvent<SheetWorkerResponse>) => {
        const data = e.data;
        // The `ready` handshake is the only response with no `id` — check that
        // instead of `kind`, which the (kind-less) `{ok:false}` variant lacks.
        if (!('id' in data)) {
            // A since-replaced worker's late `ready` must not vouch for the current one's boot.
            if (worker !== sharedWorker) return;
            workerReady = true;
            return;
        }
        const job = pendingJobs.get(data.id);
        if (!job) return;
        if (data.ok === true && data.kind === 'parse-progress') {
            job.onProgress?.(data.cells); // informational — job stays in flight
            return;
        }
        if (data.ok === true && data.kind === 'parse-part') {
            job.parts = job.parts ?? createParseAssembler();
            // Frozen here, a chunk per task, as useLazySheets would freeze them all in one render.
            data.cells.forEach((cell) => deepFreezePlain(cell));
            job.parts.add(data);
            return;
        }
        pendingJobs.delete(data.id);
        jobInFlight = false;
        job.resolve(data.ok === true && data.kind === 'parse' && data.parts ? { ...data, sheets: job.parts?.sheets() ?? [] } : data);
        postNextJob();
    };
    worker.onerror = (e) => {
        const message = e.message || 'sheet worker failed';
        failAllJobs(worker, workerReady ? new SheetWorkerCrashError(message) : new SheetWorkerBootError(message));
    };
    // A reply that can't be deserialized carries no job id, so every job is lost with it.
    worker.onmessageerror = () => failAllJobs(worker, new SheetWorkerCrashError('sheet worker message could not be deserialized'));
    sharedWorker = worker;
    return worker;
};

/** A rejected dynamic import (a stale chunk after a deploy) or a synchronous
 *  `new Worker()` throw (a CSP block) is a boot failure by definition — the
 *  worker never reached `ready` — so it must fall back the same way a
 *  post-ready crash does, not propagate as a bare error and fail every
 *  open/import outright. Exported as a test-only seam: the dynamic import
 *  itself is untestable in jsdom (see `getSharedWorker`'s own comment), but
 *  this classification is. */
export const bootWorkerOrThrow = async (create: () => Promise<Worker> | Worker): Promise<Worker> => {
    try {
        return await create();
    } catch (err) {
        throw new SheetWorkerBootError(err instanceof Error ? err.message : String(err));
    }
};

const importSheetWorker = async (): Promise<Worker> => {
    // Dynamic import so jest never parses createSheetWorker's `import.meta`
    // (the `typeof Worker` guards in the callers keep this path off in jsdom).
    const { createSheetWorker } = await import('./createSheetWorker');
    return createSheetWorker();
};
let createWorker: () => Promise<Worker> | Worker = importSheetWorker;
// The one in-flight boot, shared by every job that arrives before it settles —
// a second boot would leak a worker that keeps routing into pendingJobs.
let booting: Promise<Worker> | null = null;

const getSharedWorker = async (): Promise<Worker> => {
    if (sharedWorker) return sharedWorker;
    if (!booting) {
        booting = bootWorkerOrThrow(createWorker)
            .then(wireSharedWorker)
            .finally(() => {
                booting = null;
            });
    }
    return booting;
};

/** Test-only seam (see `wireSharedWorker`'s comment) — never called by
 *  production code. Pre-seeds `sharedWorker` so `getSharedWorker` short-
 *  circuits on its `if (sharedWorker) return sharedWorker;` check and the
 *  untestable dynamic import is never reached. */
export const __wireSharedWorkerForTests = (worker: Worker): Worker => wireSharedWorker(worker);

/** Test-only seam — swaps the untestable dynamic-import factory above so a
 *  test can drive `getSharedWorker`'s own boot path. */
export const __setSheetWorkerFactoryForTests = (create: () => Promise<Worker> | Worker): void => {
    createWorker = create;
};

const runWorkerJob = async (
    buildRequest: (id: number) => SheetWorkerRequest,
    onProgress?: (cells: number) => void,
): Promise<SheetWorkerJobResponse> => {
    await getSharedWorker();
    const id = nextJobId++;
    return new Promise<SheetWorkerJobResponse>((resolve, reject) => {
        pendingJobs.set(id, { resolve, reject, onProgress });
        jobQueue.push(buildRequest(id));
        postNextJob();
    });
};

/** Runs the same worker-side size guard on the main thread, then parses —
 *  shared by the jsdom path (no Worker at all) and a worker boot-failure
 *  fallback (small buffers only). Both must refuse a too-large file exactly
 *  like the worker would (pre-parse estimate AND the post-convert dense-
 *  grid re-check — sheetParser.worker.ts's `onmessage`), never blindly
 *  handing an oversized parse result to the main thread. */
const parseOnMainThreadGuarded = async (buffer: ArrayBuffer, mimeType?: string, fileName?: string): Promise<FortuneSheet[]> => {
    const { cells, classification } = await checkSheetSize(buffer, mimeType, fileName);
    if (classification === 'too_large') throw new SheetTooLargeError(cells, 'estimate');
    const sheets = await fileBytesToFortuneSheets(buffer, mimeType, fileName);
    const counts = mountedGridCounts(sheets);
    if (isDenseGridTooLarge(counts)) {
        const refused = tooLargeBasis(counts);
        throw new SheetTooLargeError(refused.cells, refused.basis);
    }
    return sheets;
};

export interface ParseSheetBytesOptions {
    /** Informational only — called at most once, when the worker's size guard
     *  finds the file in the 500k-2M "large" band. Never resolves anything. */
    onProgress?: (cells: number) => void;
    /** Which surface is parsing — load-failure telemetry only. Defaults to
     *  'import': every caller that isn't the fetch-driven useResolveSheets
     *  path (editor/view/preview, which passes its own context) is some
     *  flavour of file import (Reports, Ask AICA, Add to Workspace). */
    context?: SheetLoadContext;
}

/**
 * Parse spreadsheet bytes into Fortune-Sheet sheets off the main thread.
 * A structured `{ok:false}` response means the FILE is bad (too large,
 * corrupt, unsupported) — rethrown as-is, since retrying on the main thread
 * would fail identically. Only a worker BOOT failure on a small
 * buffer falls back to the main-thread parser; a mid-job crash never does.
 *
 * Every failure is logged exactly once, here, regardless of caller — this is
 * the single classification point for stage (too_large/parse/worker_boot),
 * so useResolveSheets and the import-path callers (Ask AICA, Reports import,
 * Add to Workspace) don't each need their own copy.
 */
export const parseSheetBytes = async (
    buffer: ArrayBuffer,
    mimeType?: string,
    fileName?: string,
    options: ParseSheetBytesOptions = {},
): Promise<FortuneSheet[]> => {
    const context = options.context ?? 'import';
    const startedAt = Date.now();
    const ext = fileName?.split('.').pop()?.toLowerCase() ?? '';
    const logFailure = (stage: SheetLoadStage, message: string, cellCount?: number): void =>
        logSheetLoadFailure({ stage, context, fileBytes: buffer.byteLength, cellCount, elapsedMs: Date.now() - startedAt, message, ext });
    // The one place that turns an error into a stage: a caller only supplies
    // a fallback for "neither of the above" — SheetTooLargeError/
    // SheetWorkerBootError always win regardless of what's passed.
    const logAndThrow = (err: unknown, fallbackStage: SheetLoadStage = 'parse'): never => {
        if (err instanceof SheetTooLargeError) logFailure('too_large', err.message, err.cells);
        else if (err instanceof SheetWorkerBootError) logFailure('worker_boot', err.message);
        else logFailure(fallbackStage, err instanceof Error ? err.message : String(err));
        throw err;
    };

    if (typeof Worker === 'undefined') {
        try {
            return await parseOnMainThreadGuarded(buffer, mimeType, fileName);
        } catch (err) {
            return logAndThrow(err);
        }
    }
    let response: SheetWorkerJobResponse;
    try {
        // Not passed as a transferable: keeping the buffer intact lets the
        // boot-failure fallback below reuse it.
        response = await runWorkerJob((id) => ({ id, kind: 'parse', buffer, mimeType, fileName }), options.onProgress);
    } catch (err) {
        if (err instanceof SheetWorkerBootError && buffer.byteLength < WORKER_BOOT_FALLBACK_MAX_BYTES) {
            // eslint-disable-next-line no-console
            console.warn('[SheetEditor] sheet worker failed to boot, parsing on main thread', err);
            try {
                return await parseOnMainThreadGuarded(buffer, mimeType, fileName);
            } catch (fallbackErr) {
                return logAndThrow(fallbackErr);
            }
        }
        return logAndThrow(err);
    }
    if (!response.ok) {
        if (response.code === 'TOO_LARGE') return logAndThrow(new SheetTooLargeError(response.cells ?? 0, response.basis));
        return logAndThrow(new Error(response.error));
    }
    return (response as Extract<SheetWorkerResponse, { kind: 'parse' }>).sheets;
};

/**
 * Read a File object into FortuneSheet[] (import flow: xlsx/xls/csv). Same
 * contract as converters.importFileToSheets but parsed off the main thread.
 */
export const importFileToSheets = async (file: File, options: ParseSheetBytesOptions = {}): Promise<FortuneSheet[]> => {
    const buffer = await file.arrayBuffer();
    return parseSheetBytes(buffer, file.type, file.name, options);
};

/**
 * Serialize sheets to .xlsx bytes off the main thread. Returns the raw
 * ArrayBuffer — suitable for upload or any non-download use. Falls back to the
 * main-thread converters.buildXlsxBuffer when workers are unavailable, or the
 * worker died and the workbook is small enough to rebuild on the page.
 */
export const buildXlsxBytes = async (sheets: FortuneSheet[]): Promise<ArrayBuffer> => {
    if (typeof Worker === 'undefined') return buildXlsxBufferOnMainThread(sheets);
    let response: SheetWorkerJobResponse;
    try {
        response = await runWorkerJob((id) => ({ id, kind: 'build-xlsx', sheets }));
    } catch (err) {
        if (!canBuildOnMainThread(sheets)) throw err;
        // eslint-disable-next-line no-console
        console.warn('[SheetEditor] sheet worker unavailable, serializing on main thread', err);
        return buildXlsxBufferOnMainThread(sheets);
    }
    if (!response.ok) throw new Error(response.error);
    return (response as Extract<SheetWorkerResponse, { kind: 'build-xlsx' }>).buffer;
};

/**
 * Serialize RUNTIME sheets (2D `data`, as read from `getAllSheets()`) to .xlsx
 * bytes in ONE worker round trip — the worker runs `toCelldataSheet` (the
 * per-cell walk) and `buildXlsxBuffer` together, instead of the main thread
 * walking celldata first and then cloning that result across the boundary.
 * Falls back to the main-thread converters (celldata walk + buffer build) when
 * workers are unavailable, or the worker died and the workbook is small enough
 * to rebuild on the page.
 */
export const buildXlsxFromRuntimeSheets = async (sheets: FortuneSheet[]): Promise<ArrayBuffer> => {
    if (typeof Worker === 'undefined') return buildXlsxBufferOnMainThread(sheets.map(toCelldataSheet));
    let response: SheetWorkerJobResponse;
    try {
        response = await runWorkerJob((id) => ({ id, kind: 'build-xlsx-from-runtime', sheets }));
    } catch (err) {
        if (!canBuildOnMainThread(sheets)) throw err;
        // eslint-disable-next-line no-console
        console.warn('[SheetEditor] sheet worker unavailable, serializing on main thread', err);
        return buildXlsxBufferOnMainThread(sheets.map(toCelldataSheet));
    }
    if (!response.ok) throw new Error(response.error);
    return (response as Extract<SheetWorkerResponse, { kind: 'build-xlsx-from-runtime' }>).buffer;
};

/**
 * Export sheets to .xlsx and download — serialization runs in the worker, only
 * the Blob + anchor-click happen on the main thread. Falls back to the fully
 * main-thread converters.downloadAsXlsx (also keeps existing test mocks valid)
 * — after a worker crash only for a workbook small enough to rebuild on the page.
 */
export const downloadAsXlsx = async (sheets: FortuneSheet[], fileName: string): Promise<void> => {
    if (typeof Worker === 'undefined') return downloadAsXlsxOnMainThread(sheets, fileName);
    let response: SheetWorkerJobResponse;
    try {
        response = await runWorkerJob((id) => ({ id, kind: 'build-xlsx', sheets }));
    } catch (err) {
        if (!canBuildOnMainThread(sheets)) throw err;
        // eslint-disable-next-line no-console
        console.warn('[SheetEditor] sheet worker unavailable, exporting on main thread', err);
        return downloadAsXlsxOnMainThread(sheets, fileName);
    }
    if (!response.ok) throw new Error(response.error);
    downloadXlsxBuffer((response as Extract<SheetWorkerResponse, { kind: 'build-xlsx' }>).buffer, fileName);
};
